import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fetchApi } from "../api/client";
import { Camera, Lock, Unlock, Move, Copy, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import PresetReframeModal from "../components/PresetReframeModal";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";

function CropOverlay({ cropRegion }) {
    if (!cropRegion) return null;
    const { x, y, w, h } = cropRegion;
    return (
        <div className="crop-overlay">
            <div className="crop-dim" style={{ top: 0, left: 0, right: 0, height: `${y * 100}%` }} />
            <div className="crop-dim" style={{ top: `${(y + h) * 100}%`, left: 0, right: 0, bottom: 0 }} />
            <div className="crop-dim" style={{ top: `${y * 100}%`, left: 0, width: `${x * 100}%`, height: `${h * 100}%` }} />
            <div className="crop-dim" style={{ top: `${y * 100}%`, right: 0, left: `${(x + w) * 100}%`, height: `${h * 100}%` }} />
            <div className="crop-border" style={{
                top: `${y * 100}%`, left: `${x * 100}%`,
                width: `${w * 100}%`, height: `${h * 100}%`
            }} />
        </div>
    );
}

const STATUS_CONFIG = {
    live:             { label: "LIVE",            bg: "rgba(34,197,94,0.15)",   color: "#22c55e", sortOrder: 3 },
    in_progress:      { label: "IN PROGRESS",     bg: "rgba(250,204,21,0.15)",  color: "#facc15", sortOrder: 1 },
    draft:            { label: "DRAFT",           bg: "rgba(239,68,68,0.15)",   color: "#ef4444", sortOrder: 2 },
    ready_for_review: { label: "READY TO REVIEW", bg: "rgba(168,85,247,0.15)",  color: "#a855f7", sortOrder: 0 },
    has_camera:       { label: "HAS CAMERA",      bg: "rgba(59,130,246,0.15)",  color: "#3b82f6", sortOrder: 4 },
    not_started:      { label: "NO CAMERA",       bg: "rgba(100,100,100,0.1)",  color: "#888",    sortOrder: 5 },
};

function ProgressBar({ locked, total }) {
    if (total === 0) return <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>--</span>;
    const pct = Math.round((locked / total) * 100);
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
            <div style={{ flex: 1, background: "var(--bg-tertiary, #2a2a2e)", borderRadius: 3, height: 6, overflow: "hidden" }}>
                <div style={{
                    width: `${pct}%`, height: "100%", borderRadius: 3,
                    background: pct === 100 ? "#22c55e" : pct > 0 ? "#facc15" : "transparent",
                    transition: "width 0.3s"
                }} />
            </div>
            <span style={{ fontSize: 12, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                {locked}/{total}
            </span>
        </div>
    );
}

function SiteSelector() {
    const navigate = useNavigate();
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [relayStatus, setRelayStatus] = useState(null);
    const [filters, setFilters] = useState({ states: [], rpls: [] });
    const [stateFilter, setStateFilter] = useState("");
    const [rplFilter, setRplFilter] = useState("");
    const [search, setSearch] = useState("");
    const [sortCol, setSortCol] = useState("siteId");
    const [sortDir, setSortDir] = useState("asc");
    const [showAll, setShowAll] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [siteData, filterData, relay] = await Promise.all([
                api.onboarding.sites({ state: stateFilter, rpl: rplFilter, search }),
                api.onboarding.filters(),
                fetchApi("/relay/status").catch(() => ({ connected: false }))
            ]);
            setSites(siteData.sites || []);
            setFilters(filterData);
            setRelayStatus(relay);
        } catch (e) {
            console.error("Failed to load onboarding data", e);
        } finally {
            setLoading(false);
        }
    }, [stateFilter, rplFilter, search]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortCol(col);
            setSortDir("asc");
        }
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return null;
        return sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
    };

    const filteredSites = useMemo(() => {
        let list = [...sites];
        if (!showAll) {
            list = list.filter(s => s.status !== "not_started");
        }
        list.sort((a, b) => {
            let cmp = 0;
            if (sortCol === "siteId") cmp = (a.siteId || "").localeCompare(b.siteId || "", undefined, { numeric: true });
            else if (sortCol === "name") cmp = (a.name || "").localeCompare(b.name || "");
            else if (sortCol === "state") cmp = (a.stateCode || "").localeCompare(b.stateCode || "");
            else if (sortCol === "presets") cmp = a.presetCount - b.presetCount;
            else if (sortCol === "locked") cmp = a.lockedCount - b.lockedCount;
            else if (sortCol === "progress") {
                const pa = a.presetCount > 0 ? a.lockedCount / a.presetCount : -1;
                const pb = b.presetCount > 0 ? b.lockedCount / b.presetCount : -1;
                cmp = pa - pb;
            }
            else if (sortCol === "status") cmp = (STATUS_CONFIG[a.status]?.sortOrder ?? 9) - (STATUS_CONFIG[b.status]?.sortOrder ?? 9);
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [sites, showAll, sortCol, sortDir]);

    // Summary counts
    const counts = useMemo(() => {
        const c = { live: 0, in_progress: 0, draft: 0, ready_for_review: 0, has_camera: 0, not_started: 0 };
        sites.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
        return c;
    }, [sites]);

    // NB: do NOT early-return the whole page on `loading` — every keystroke in the search box triggers a
    // reload (loading=true), which would unmount+remount the filter bar and steal focus from the input.
    // Keep the filters mounted and gate only the results table below (same as the POS Fraud page).

    const thStyle = {
        padding: "8px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.5, color: "var(--text-secondary)", borderBottom: "1px solid var(--border)",
        cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", textAlign: "left",
    };
    const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))" };

    return (
        <div>
            <div className="page-header">
                <h2>Store Onboarding</h2>
            </div>

            {/* Summary cards */}
            <div style={{ display: "flex", gap: 10, padding: "0 0 16px", flexWrap: "wrap" }}>
                {[
                    { key: "live", label: "Live", count: counts.live },
                    { key: "in_progress", label: "In Progress", count: counts.in_progress },
                    { key: "ready_for_review", label: "Ready to Review", count: counts.ready_for_review },
                    { key: "draft", label: "Draft", count: counts.draft },
                    { key: "has_camera", label: "Has Camera", count: counts.has_camera },
                ].map(c => (
                    <div key={c.key} className="card" style={{ padding: "10px 16px", minWidth: 100, textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: STATUS_CONFIG[c.key].color }}>{c.count}</div>
                        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>{c.label}</div>
                    </div>
                ))}
                <div className="card" style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                        width: 8, height: 8, borderRadius: "50%", display: "inline-block",
                        background: relayStatus?.connected ? "#4caf50" : "#f44336"
                    }} />
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        PTZ Relay {relayStatus?.connected ? "Connected" : "Offline"}
                    </span>
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <select className="filter-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All States</option>
                    {filters.states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                </select>
                <select className="filter-select" value={rplFilter} onChange={e => setRplFilter(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="">All RPLs</option>
                    {filters.rpls.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input
                    type="text" className="filter-select" placeholder="Search site..."
                    value={search} onChange={e => setSearch(e.target.value)}
                    style={{ fontSize: 12, minWidth: 160 }}
                />
                <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer", marginLeft: "auto" }}>
                    <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
                    Show all sites ({sites.length})
                </label>
                <Link to="/help/onboarding" style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "5px 12px", fontSize: 12, fontWeight: 500,
                    border: "1px solid var(--border)", borderRadius: 6,
                    color: "var(--text-secondary)", textDecoration: "none",
                    background: "var(--bg-secondary)", whiteSpace: "nowrap"
                }}>? Guide</Link>
            </div>

            {/* Sites table */}
            {loading ? <LoadingSpinner message="Loading sites..." /> : (
            <div className="card" style={{ overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thStyle} onClick={() => handleSort("siteId")}>Site <SortIcon col="siteId" /></th>
                            <th style={thStyle} onClick={() => handleSort("name")}>Name <SortIcon col="name" /></th>
                            <th style={thStyle} onClick={() => handleSort("state")}>State <SortIcon col="state" /></th>
                            <th style={thStyle} onClick={() => handleSort("presets")}>Presets <SortIcon col="presets" /></th>
                            <th style={thStyle} onClick={() => handleSort("progress")}>Progress <SortIcon col="progress" /></th>
                            <th style={thStyle} onClick={() => handleSort("status")}>Status <SortIcon col="status" /></th>
                            <th style={{ ...thStyle, cursor: "default" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSites.length === 0 && (
                            <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)", padding: 32 }}>
                                No sites match the current filters
                            </td></tr>
                        )}
                        {filteredSites.map(s => {
                            const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.not_started;
                            return (
                                <tr key={s.siteId}
                                    style={{ cursor: s.presetCount > 0 || s.hasPtz || s.sweepCount > 0 ? "pointer" : "default" }}
                                    onClick={() => { if (s.presetCount > 0 || s.sweepCount > 0) navigate(`/onboarding/${s.siteId}`); }}
                                    onMouseEnter={e => { if (s.presetCount > 0 || s.hasPtz || s.sweepCount > 0) e.currentTarget.style.background = "var(--bg-hover)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                                >
                                    <td style={tdStyle}><strong>{s.siteId}</strong></td>
                                    <td style={tdStyle}>{s.name || "--"}</td>
                                    <td style={tdStyle}>{s.stateCode || "--"}</td>
                                    <td style={tdStyle}>
                                        {s.presetCount > 0
                                            ? <span>{s.lockedCount}/{s.presetCount}</span>
                                            : <span style={{ color: "var(--text-secondary)" }}>--</span>
                                        }
                                    </td>
                                    <td style={tdStyle}><ProgressBar locked={s.lockedCount} total={s.presetCount} /></td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                                            background: cfg.bg, color: cfg.color
                                        }}>{cfg.label}</span>
                                    </td>
                                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                                        {(s.presetCount > 0 || s.status === "has_camera" || s.status === "ready_for_review") && (
                                            <button
                                                className="preset-action-btn"
                                                onClick={() => navigate(`/onboarding/${s.siteId}`)}
                                                style={{ padding: "4px 10px", fontSize: 11 }}
                                            >{s.status === "live" ? "Manage" : s.status === "ready_for_review" ? "Review" : s.status === "has_camera" ? "Onboard" : "Continue"}</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
}

function StoreOnboarding() {
    const { siteId } = useParams();
    const { user, hasPermission } = useAuth();
    const canManagePresets = hasPermission("onboarding.manage_presets");
    const [presets, setPresets] = useState([]);
    const [storeName, setStoreName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reframePreset, setReframePreset] = useState(null);
    const [relayStatus, setRelayStatus] = useState(null);
    const [modalImage, setModalImage] = useState(null);

    useEffect(() => {
        if (!siteId) return;
        setLoading(true);
        Promise.all([
            api.presetsByStore(siteId),
            fetchApi("/relay/status").catch(() => ({ connected: false }))
        ]).then(([data, relay]) => {
            setPresets(data.presets || []);
            setStoreName(data.store?.name || `Site ${siteId}`);
            setRelayStatus(relay);
            setError(null);
        }).catch(err => setError(err.message))
          .finally(() => setLoading(false));
    }, [siteId]);

    // No siteId — show site selector
    if (!siteId) return <SiteSelector />;

    const loadPresets = () => {
        setLoading(true);
        Promise.all([
            api.presetsByStore(siteId),
            fetchApi("/relay/status").catch(() => ({ connected: false }))
        ]).then(([data, relay]) => {
            setPresets(data.presets || []);
            setStoreName(data.store?.name || `Site ${siteId}`);
            setRelayStatus(relay);
            setError(null);
        }).catch(err => setError(err.message))
          .finally(() => setLoading(false));
    };

    const handleNudge = async (delta) => {
        return await api.nudgePreset(siteId, reframePreset.presetName, delta);
    };

    const handleConfirm = async (ptz) => {
        await api.confirmPreset(siteId, reframePreset.presetName, ptz);
        setReframePreset(null);
        loadPresets();
    };

    const handleEditSave = async (data) => {
        await api.updatePreset(siteId, reframePreset.presetName, data);
    };

    const handleDelete = async (presetName) => {
        if (!window.confirm(`Delete preset ${presetName}? This cannot be undone.`)) return;
        try {
            await api.dropPreset(siteId, presetName);
            loadPresets();
        } catch (err) {
            alert(`Delete failed: ${err.message}`);
        }
    };

    const [unlockingPreset, setUnlockingPreset] = useState(null);
    const handleUnlock = async (presetName) => {
        if (!window.confirm(`Unlock preset ${presetName}? It will move to the "Needs Framing" section.`)) return;
        setUnlockingPreset(presetName);
        try {
            await api.togglePresetLock(siteId, presetName);
            loadPresets();
        } catch (err) {
            alert(`Unlock failed: ${err.message}`);
        } finally {
            setUnlockingPreset(null);
        }
    };

    const handleClearUnlocked = async () => {
        const unlocked = presets.filter(p => !p.locked);
        if (unlocked.length === 0) { alert("No unlocked presets to clear."); return; }
        if (!window.confirm(`Delete ${unlocked.length} unlocked preset(s)? Locked presets will not be affected.`)) return;
        try {
            const result = await api.clearUnlockedPresets(siteId);
            alert(`Cleared ${result.deleted} unlocked preset(s).`);
            loadPresets();
        } catch (err) {
            alert(`Failed to clear presets: ${err.message}`);
        }
    };

    const handleDuplicate = async (presetName) => {
        try {
            const result = await api.duplicatePreset(siteId, presetName);
            loadPresets();
            if (result.newPresetName) {
                alert(`Created ${result.newPresetName}`);
            }
        } catch (err) {
            alert(`Duplicate failed: ${err.message}`);
        }
    };

    if (loading) return <LoadingSpinner message="Loading presets..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load presets</h3>
                <p>{error}</p>
                <Link to="/onboarding" style={{ color: "var(--accent)" }}>Back to Site List</Link>
            </div>
        );
    }

    const draftPresets = presets.filter(p => !p.locked);
    const lockedPresets = presets.filter(p => p.locked);
    const progress = presets.length > 0 ? Math.round((lockedPresets.length / presets.length) * 100) : 0;

    return (
        <div>
            <div className="page-header">
                <p style={{ marginBottom: 4 }}>
                    <Link to="/onboarding" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 13 }}>
                        &larr; Back to Site List
                    </Link>
                </p>
                <h2>{storeName}</h2>
                <p style={{ color: "var(--text-secondary)" }}>
                    Site #{siteId} &mdash; {lockedPresets.length}/{presets.length} presets locked ({progress}%)
                </p>
            </div>

            {/* Relay status + progress */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div className="card" style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
                    <span style={{
                        width: 8, height: 8, borderRadius: "50%", display: "inline-block",
                        background: relayStatus?.connected ? "#4caf50" : "#f44336"
                    }} />
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        PTZ Relay {relayStatus?.connected ? "connected" : "offline"}
                    </span>
                </div>
                {presets.length > 0 && (
                    <div className="card" style={{ padding: "10px 16px", flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4, color: "var(--text-secondary)" }}>
                            <span>Progress</span>
                            <span>{lockedPresets.length}/{presets.length} locked</span>
                        </div>
                        <div style={{ background: "var(--bg-tertiary, #2a2a2e)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                            <div style={{
                                width: `${progress}%`, height: "100%", borderRadius: 4,
                                background: progress === 100 ? "#4caf50" : "var(--accent)",
                                transition: "width 0.3s"
                            }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Draft presets — need framing */}
            {draftPresets.length > 0 && (
                <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 10px" }}>
                        <h3 style={{ margin: 0, fontSize: 14, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 1 }}>
                            Needs Framing ({draftPresets.length})
                        </h3>
                        <button
                            onClick={handleClearUnlocked}
                            style={{
                                padding: "6px 14px", fontSize: 12, fontWeight: 700,
                                background: "transparent", color: "#ef4444", border: "1px solid #ef4444",
                                borderRadius: 6, cursor: "pointer",
                            }}
                        >
                            Clear All Unlocked
                        </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                        {draftPresets.map(p => (
                            <div key={p.id} className="preset-card">
                                <div className="preset-card-image-wrap">
                                    {p.referenceImageUrl ? (
                                        <>
                                            <img
                                                className="preset-card-image"
                                                src={p.referenceImageUrl}
                                                alt={p.presetName}
                                                onClick={() => setModalImage({ src: p.referenceImageUrl, alt: p.presetName })}
                                            />
                                            <CropOverlay cropRegion={p.cropRegion} />
                                        </>
                                    ) : (
                                        <div className="preset-card-placeholder">
                                            <Camera size={32} />
                                            <span>Draft &mdash; needs framing</span>
                                        </div>
                                    )}
                                </div>
                                <div className="preset-card-body">
                                    <div className="preset-card-header">
                                        <span className="preset-card-name">{p.presetName}</span>
                                    </div>
                                    <div className="preset-card-detail">
                                        <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                                            {p.fixtureType ? p.fixtureType.replace(/_/g, " ") : "Unknown"}
                                        </span>
                                    </div>
                                    <div className="preset-card-lock">
                                        <Unlock size={12} />
                                        <span style={{ color: "var(--yellow)" }}>Unlocked</span>
                                    </div>
                                    <div className="preset-card-actions">
                                        <button
                                            className="preset-action-btn"
                                            onClick={() => setReframePreset(p)}
                                            style={{ flex: 1 }}
                                        >
                                            <Move size={13} /> Re-frame &amp; Lock
                                        </button>
                                        <button
                                            className="preset-action-btn"
                                            onClick={() => handleDuplicate(p.presetName)}
                                            title="Duplicate preset"
                                            style={{ padding: "4px 8px" }}
                                        >
                                            <Copy size={13} />
                                        </button>
                                        <button
                                            className="preset-action-btn"
                                            onClick={() => handleDelete(p.presetName)}
                                            title="Delete preset"
                                            style={{ padding: "4px 8px", color: "var(--red, #d32f2f)" }}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Locked presets — done */}
            {lockedPresets.length > 0 && (
                <>
                    <h3 style={{ margin: "24px 0 10px", fontSize: 14, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 1 }}>
                        Locked ({lockedPresets.length})
                    </h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                        {lockedPresets.map(p => (
                            <div key={p.id} className="preset-card" style={{ opacity: 0.7 }}>
                                <div className="preset-card-image-wrap">
                                    {p.referenceImageUrl ? (
                                        <>
                                            <img
                                                className="preset-card-image"
                                                src={p.referenceImageUrl}
                                                alt={p.presetName}
                                                onClick={() => setModalImage({ src: p.referenceImageUrl, alt: p.presetName })}
                                            />
                                            <CropOverlay cropRegion={p.cropRegion} />
                                        </>
                                    ) : (
                                        <div className="preset-card-placeholder">
                                            <Camera size={32} />
                                        </div>
                                    )}
                                </div>
                                <div className="preset-card-body">
                                    <div className="preset-card-header">
                                        <span className="preset-card-name">{p.presetName}</span>
                                    </div>
                                    <div className="preset-card-detail">
                                        <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                                            {p.fixtureType ? p.fixtureType.replace(/_/g, " ") : "Unknown"}
                                        </span>
                                    </div>
                                    <div className="preset-card-lock">
                                        <Lock size={12} />
                                        <span>Locked by {p.lockedBy || "system"}</span>
                                    </div>
                                    <div className="preset-card-actions">
                                        <button
                                            className="preset-action-btn"
                                            onClick={() => handleUnlock(p.presetName)}
                                            disabled={unlockingPreset === p.presetName}
                                            style={{ color: "var(--yellow, #f59e0b)" }}
                                        >
                                            <Unlock size={13} /> {unlockingPreset === p.presetName ? "..." : "Unlock"}
                                        </button>
                                        {canManagePresets && (
                                            <button
                                                className="preset-action-btn"
                                                onClick={() => setReframePreset(p)}
                                            >
                                                <Move size={13} /> Re-frame
                                            </button>
                                        )}
                                        <button
                                            className="preset-action-btn"
                                            onClick={() => handleDuplicate(p.presetName)}
                                            title="Duplicate as unlocked copy"
                                            style={{ padding: "4px 8px" }}
                                        >
                                            <Copy size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {presets.length === 0 && (
                <div className="empty-state">
                    <h3>No Presets</h3>
                    <p>This site has no camera presets configured yet.</p>
                </div>
            )}

            {/* Image lightbox */}
            {modalImage && (
                <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
                    <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setModalImage(null)}>ESC</button>
                        <img className="image-modal-img" src={modalImage.src} alt={modalImage.alt} />
                    </div>
                </div>
            )}

            {/* Reframe modal — enhanced for onboarding with classification + crop */}
            {reframePreset && (
                <PresetReframeModal
                    preset={reframePreset}
                    siteId={siteId}
                    onNudge={handleNudge}
                    onConfirm={handleConfirm}
                    onSaveEdit={handleEditSave}
                    showEditPanel={true}
                    onClose={() => setReframePreset(null)}
                />
            )}
        </div>
    );
}

export default StoreOnboarding;
